import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { theme } from 'app/theme'
import { rgba } from 'emotion-rgba'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  label?: string
  title?: string
}

export const Banner = memo(function Banner({ label, title }: Props) {
  const [animObjectRef, isAnimObjectVisible] = useInView({
    threshold: 0.25,
    triggerOnce: true,
  })

  if (!label || !title) {
    return null
  }

  return (
    <Container
      className={isAnimObjectVisible ? 'visible' : undefined}
      dial={5}
      ref={animObjectRef}
    >
      {label ? <Label>{label}</Label> : null}

      {title ? <Title>{title}</Title> : null}
    </Container>
  )
})

const Container = styled(FlexBox)`
  width: 11.25rem;
  height: 11.25rem;
  background-color: ${rgba(theme.colors.variants.neutralLight5, 0.1)};
  backdrop-filter: blur(1.25rem) brightness(0.8);
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  opacity: 0;
  position: absolute;
  top: 7.25rem;
  right: 10vw;
  text-align: center;
  transform: scale(0);
  transition: 0.6s ease-in-out;
  z-index: 3;
  &.visible {
    opacity: 1;
    transform: none;
  }
  &:before {
    content: '';
    width: 10.25rem;
    height: 10.25rem;
    border: 0.0625rem solid ${rgba(theme.colors.variants.neutralLight5, 0.1)};
    border-radius: 50%;
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
  }

  @supports not ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
    background-color: ${rgba(theme.colors.variants.neutralLight5, 0.8)};
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    background-color: ${rgba(theme.colors.variants.neutralLight5, 0.8)};
  }

  @media (max-width: 1199px) {
    width: 100%;
    height: auto;
    border-radius: 0;
    padding: 0.75rem 1.875rem;
    top: auto;
    bottom: 0;
    right: 0;
    text-align: center;
    transform: none;
    z-index: 3;
    &:before {
      display: none;
    }
  }
`

const Label = styled.div`
  max-width: 3.75rem;
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.75rem;
  letter-spacing: 0.15em;
  line-height: 0.8125rem;
  margin-bottom: 0.75rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    max-width: none;
    margin-bottom: 0.25rem;
  }
`

const Title = styled.div`
  max-width: 5.9375rem;
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.875rem;
  font-weight: 300;
  line-height: 1.25rem;

  @media (max-width: 1199px) {
    max-width: none;
  }
`
